import React, {useState, useEffect,
	useMemo}								from 'react';
import {useNavigate, useLocation}			from 'react-router-dom';
import {Routes, Route}						from 'react-router-dom';

import {useAppSelector, useAppDispatch}		from './app/hooks';
import {checkIsConnected, logout}			from './redux/user/user';
import {getDevicesApi}						from './redux/devices/devices';
import {getContractsApi}					from './redux/contracts/contracts';
import {getCompanyUsersApi}					from './redux/companyUsers/companyUsers';
import {getCompanyApi}						from './redux/company/company';
import {getAddressApi}						from './redux/address/address';
import {getContractsNotValalaApi}			from './redux/contractsNotValala/contractsNotValala';
import {closeNotification}					from './redux/notifications/notifications';
import {hideNotification}					from './redux/documents/documents';
import {getOrdersApi}						from './redux/order/order';
import {getSupportsApi}						from './redux/support/support';
import {setFullBasket}						from './redux/basket/basket';
import {getMobileLineApi} from './redux/mobileLines/mobileLines';

import Menu									from './Menu/Menu';
import Dashboard							from './Dashboard/Dashboard';
import MainDevices							from './Devices/Main';
import MainUsers							from './Users/Main';
import MainContracts						from './Contracts/Main';
import MainOrder							from './Order/Main';
import MainSupport							from './Support/Main';
import Settings								from './Settings/Settings';
import MainMobileLines						from './MobileLines/Main';
import Login								from './Login/Login';
import NotificationCard						from './ressources/components/NotificationCard';

import useWindowSize						from './ressources/functions/useWindowSize';
import {ft_formatBytes}						from './ressources/functions/format';
import {ft_checkRight}						from './ressources/functions/userFunction';
import {ft_isContractNeedAction}			from './ressources/functions/contractFunction';
import {getLocalStorage, setLocalStorage}	from './ressources/functions/localStorage';

import img_file								from './ressources/images/icon/file.svg';

import './App.css';


function	App() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const isAuth = useAppSelector(state => state.user.isAuthenticated);
	const reload = useAppSelector(state => state.user.reload);
	const documentsUpload = useAppSelector(state => state.documents.uploadDocument);
	const contracts = useAppSelector(state => state.contracts.data);
	const contractsNotValala = useAppSelector(state => state.contractsNotValala.data);
	const notifications = useAppSelector(state => state.notifications.list);

	const [isInit, set_isInit] = useState(false);
	const [limitedOrder, set_limitedOrder] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const sizes = useWindowSize();

	const notifContract = useMemo(() => {
		let found = false;

		if (!found) {
			contracts.forEach((contract) => {
				for (let i = 0; !found && contract.funderList[i]; i++)
					found = found || ft_isContractNeedAction(contract, i);
			});
		}

		return found;
	}, [contracts, contractsNotValala]);

	useEffect(() => {
		initApp();
	}, []);

	useEffect(() => {
		if (location.pathname === '/resetPassword')
			dispatch(logout());

		if (isInit && !isAuth) {
			if (location.pathname.startsWith('/order/') && location.pathname.length === 33 && location.search.startsWith('?orderToken='))
				return set_limitedOrder(true);

			if (document.referrer && !location.search.includes('referer='))
				navigate(`?referer=${document.referrer}`);
		}
		set_limitedOrder(false);
	}, [isInit, location.pathname, location.search]);

	useEffect(() => {
		if (isInit && isAuth)
			initApp();

		if (!isAuth)
			return;

		if (location.pathname === '/login')
			navigate('/home');

		const save = getLocalStorage('saveCart');

		if (!save?.data)
			return;
		dispatch(setFullBasket(save));
		setLocalStorage('saveCart', '');
	}, [isAuth, reload]);

	useEffect(() => {
		if (!user?.right)
			return;

		const limited = !ft_checkRight(user, 'company.read');

		dispatch(getDevicesApi(user));
		dispatch(getMobileLineApi(user));
		dispatch(getSupportsApi(user));
		dispatch(getCompanyUsersApi(user));

		if (!limited) {
			dispatch(getOrdersApi(user));
			dispatch(getCompanyApi(user));
			dispatch(getAddressApi(user));
			dispatch(getContractsApi(user));
			dispatch(getContractsNotValalaApi(user));
		}
	}, [user]);

	async function initApp() {
		const params = new URLSearchParams(window.location.search);
		const force = {
			email: params.get('email') || '',
			token: params.get('token') || ''
		};

		if (force.email && force.token) {
			await dispatch(checkIsConnected(force));
			navigate('/');
		} else
			await dispatch(checkIsConnected());

		set_isInit(true);
	}

	function renderNotifications() {
		const render: React.ReactElement[] = [];

		documentsUpload.forEach((document, i) => {
			if (document.hideNotification || typeof document.percent !== 'number' || document.percent === 100)
				return;
			render.push(
				<NotificationCard
					icon={img_file}
					type={'primary'}
					key={document.id}
					title={document.filename}
					text={ft_formatBytes(document.size)}
					onClick={() => dispatch(hideNotification(i))}
					percent={typeof document.percent === 'number' ? document.percent : 100}
				/>
			);
		});

		notifications.forEach((notification) => {
			render.push(
				<NotificationCard
					key={notification.id}
					type={notification.type}
					icon={notification.icon}
					title={notification.title}
					text={notification.text}
					onClick={() => dispatch(closeNotification(notification.id))}
				/>
			);
		});

		return (
			<div className={'notificationContainer'}>
				{render}
			</div>
		);
	}

	if (!isInit || ((!isAuth || !user) && !limitedOrder)) {
		return (
			<div className={'mainLogin'} >
				<Routes>
					{isInit && <Route path={'*'} element={<Login />} />}
				</Routes>
			</div>
		);
	}

	if (limitedOrder && (!isAuth || !user)) {
		return (
			<div
				id={'main'}
				className={'main'}
			>
				<Routes>
					<Route path={'*'} element={<Menu notifContract={false} />}>
						<Route
							path={'order/*'}
							element={<MainOrder limited />}
						/>
					</Route>
				</Routes>
				{renderNotifications()}
			</div>
		);
	}

	if (sizes.width && sizes.width < 767 && !location.pathname.startsWith('/order'))
		navigate('/order');

	return (
		<div
			id={'main'}
			className={'main'}
		>
			<Routes>
				<Route path={'*'} element={<Menu notifContract={notifContract} />}>
					<Route path={''} element={<Dashboard />} />
					<Route path={'home'} element={<Dashboard />} />
					<Route path={'devices/*'} element={<MainDevices />} />
					<Route path={'mobileLines/*'} element={<MainMobileLines />} />
					<Route
						path={'users/*'}
						element={ft_checkRight(user, 'companyUsers.read') ? <MainUsers /> : <Dashboard />}
					/>
					<Route
						path={'contracts/*'}
						element={ft_checkRight(user, 'company.read') ? <MainContracts notifContract={notifContract} /> : <Dashboard />}
					/>
					<Route
						path={'order/*'}
						element={ft_checkRight(user, 'company.read') ? <MainOrder /> : <Dashboard />}
					/>
					<Route path={'support/*'} element={<MainSupport />} />
					<Route path={'settings/*'} element={<Settings />} />
				</Route>
			</Routes>
			{renderNotifications()}
		</div>
	);
}

export default App;
